<template>
    <LoginBase>
        <h6 class="q-mb-none q-mt-sm text-weight-bold">Войдите в свой аккаунт</h6>
        <q-form class="column q-gutter-y-md full-width" ref="formRef" @submit="login" @reset="onFormReset">
            <q-input
                v-model.trim="loginData.login"
                label="Логин или email"
                :rules="[rules.loginRequired]"
                lazy-rules
                :error="!!loginError"
                dense
                outlined
                autocorrect="off"
                autocapitalize="off"
                autocomplete="off"
                spellcheck="false"
                data-cy="auth-login"
            />
            <q-input
                ref="password"
                v-model="loginData.password"
                label="Пароль"
                :type="hidePassword ? 'password' : 'text'"
                :rules="[rules.passRequired]"
                :error="!!loginError"
                :error-message="loginError || undefined"
                lazy-rules
                dense
                outlined
                autocorrect="off"
                autocapitalize="off"
                autocomplete="off"
                spellcheck="false"
                data-cy="auth-password"
                @keydown.enter="validateAndLogin"
            >
                <template v-slot:append>
                    <q-icon
                        :name="hidePassword ? 'visibility_off' : 'visibility'"
                        class="cursor-pointer"
                        @click="hidePassword = !hidePassword"
                    />
                </template>
            </q-input>

            <q-btn
                type="submit"
                data-cy="auth-submit"
                :loading="loading"
                label="Войти"
                stretch
                text-color="white"
                color="secondary"
            />
        </q-form>
        <router-link class="q-mt-sm" :to="{ name: 'ResetPasswordPage' }">Забыли пароль?</router-link>
    </LoginBase>
</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent, nextTick, onMounted, reactive, ref } from 'vue';
import LoginBase from '@platform/layouts/login/login-base.vue';
import { useQuasar, type QForm } from 'quasar';
import { customRuleMsg, predefined } from '@platform/components/docflow/common/form-generator/form-rules';
import { UserStore } from '@platform/store';
import { useRouter } from 'vue-router';
import { useRequestWrapper } from '@platform/shared/hooks/use-request-wrapper';
import { authLogin, type AuthLoginCallback } from '@platform/layouts/login/auth-login';
import { resolveLoginErrorPresentation } from '@platform/layouts/login/resolve-login-error-message';

const defaultLoginData = {
    login: '',
    password: '',
} as const;

export default defineComponent({
    name: 'LoginPage',
    components: { LoginBase },
    props: {
        authLoginCallback: { type: Function as PropType<AuthLoginCallback>, required: false },
    },
    setup(props) {
        const router = useRouter();
        const $q = useQuasar();
        const loginError = ref<string | null>(null);
        const formRef = ref<QForm | null>();
        const hidePassword = ref(true);
        const autoLogin = UserStore.userState.autoLogin;
        const loginData = reactive(
            autoLogin ?? {
                ...defaultLoginData,
            }
        );
        const onFormReset = () => {
            Object.assign(loginData, defaultLoginData);
        };
        const { loading, handler: login } = useRequestWrapper(
            async () => {
                await (props.authLoginCallback ?? authLogin)(loginData, {
                    loginError: loginError,
                });

                let redirectTo = '/';
                const redirect = localStorage.getItem('login_redirect');
                if (redirect) {
                    const redirInfo: { when: string; redirectTo: string } = JSON.parse(redirect);
                    if (redirInfo && Date.now() - Date.parse(redirInfo.when) < 1000 * 60 * 5) {
                        redirectTo = redirInfo.redirectTo;
                    }
                    localStorage.removeItem('login_redirect');
                }

                await router.replace(redirectTo);
            },
            { 401: 'Неверный логин или пароль' },
            (msg, error) => {
                const { inline, toast } = resolveLoginErrorPresentation(msg, error);
                loginError.value = inline;
                if (toast) {
                    $q.notify({
                        position: 'top-right',
                        type: 'error',
                        color: 'red',
                        textColor: 'white',
                        message: toast,
                        icon: 'warning',
                    });
                }
            }
        );

        const validateAndLogin = async () => {
            (await formRef.value?.validate(true)) && (await login());
        };
        onMounted(async () => {
            if (!autoLogin) return;
            await nextTick();
            await new Promise((r) => setTimeout(r, 500));
            await validateAndLogin();
        });
        return {
            loginData,
            hidePassword,
            formRef,
            login,
            loading,
            validateAndLogin,
            onFormReset,
            loginError,
            rules: {
                loginRequired: customRuleMsg(predefined.required, 'Введите логин'),
                passRequired: customRuleMsg(predefined.required, 'Введите пароль'),
            },
        };
    },
});
</script>
